/*
 * section-faq.js — Slice 18 Phase B.
 *
 * FAQ-specific behaviors layered on top of Phase A's
 * component-shared-accordions.js (which handles disclosure toggle).
 *
 * Behaviors (V1 — IntersectionObserver active-tab tracking deliberately
 * omitted; active tab state updates on click only in both jumplink and
 * filter modes — click-vs-IO race condition not worth solving for V1):
 *   1. Tab generation (HOTFIX 2026-05-28) — generateTabs(section) scans
 *      .faq-category divs and injects per-category <button> elements
 *      into [data-faq-tabs] container after the static "All" button.
 *      Sums per-category counts to update [data-faq-tab-count-all].
 *      Replaces the original Liquid section.blocks iteration which
 *      produced zero matches in production despite identical syntax
 *      working in sections/header.liquid (root cause not diagnosed).
 *      Tab structure mirrors what the original Liquid loop would have
 *      produced. Click delegation already operates at document level
 *      so newly-injected buttons work without rebinding.
 *   2. Search filter — input event on [data-faq-search]; toggles
 *      .is-hidden on [data-acc-item] based on question+answer text
 *      substring match (case-insensitive). Hides .faq-category when
 *      all items hidden. Shows [data-faq-no-results] when no items
 *      visible. Respects active filter-tab when tab_behavior=filter.
 *      Reads via textContent (not innerText) so closed-state accordion
 *      panels — which carry the [hidden] attribute → display:none —
 *      still contribute their answer text to the haystack.
 *   3. Tab interaction — click event delegated on [data-faq-tab].
 *      Jumplink mode (data-tab-behavior=jumplink): smooth-scroll to
 *      #cat-{anchor}; "all" scrolls to section top.
 *      Filter mode (data-tab-behavior=filter): set data-active-tab on
 *      section root + .is-active-cat on matching category; re-run
 *      search filter to combine with active tab.
 *   4. Mobile tab arrows — setupTabArrows() scrolls the tab strip
 *      and auto-hides each arrow at its scroll boundary; mobile only
 *      via CSS (arrows are display:none above 749px).
 *   5. Init — DOMContentLoaded + shopify:section:load. Scan for
 *      [data-section-faq]; per-instance init runs generateTabs
 *      BEFORE attachSearch so tabs exist when search would interact
 *      with them.
 *
 * IIFE-dedup-guarded per Rule J12. Single document-level click
 * listener (event delegation). No window.<theme> namespace pollution
 * per Rule J8.
 *
 * Rules in force:
 *   Rule J1  — addEventListener only
 *   Rule J7  — document-level listener
 *   Rule J8  — no window.* namespace pollution; pure IIFE
 *   Rule J12 — IIFE-dedup guard for multi-instance safety
 */
(function () {
  if (window.__BLOCKDEDUP_SECTION_FAQ__) return;
  window.__BLOCKDEDUP_SECTION_FAQ__ = true;

  /* ---------- Tab generation (hotfix 2026-05-28) ---------- */

  function generateTabs(section) {
    var container = section.querySelector('[data-faq-tabs]');
    if (!container) return;

    var allCountSpan = container.querySelector('[data-faq-tab-count-all]');
    var showCounts = !!allCountSpan;

    var categories = section.querySelectorAll('.faq-category');
    var total = 0;

    for (var i = 0; i < categories.length; i++) {
      var cat = categories[i];
      var handle = cat.getAttribute('data-cat-name') || '';
      var anchor = cat.getAttribute('data-cat-anchor') || handle;
      var items = cat.querySelectorAll('[data-acc-item]');
      var count = items.length;
      total += count;

      var nameEl = cat.querySelector('.faq-category__name');
      var prefixEl = cat.querySelector('.faq-category__prefix');
      var name = nameEl ? nameEl.textContent.trim() : handle;
      var prefix = prefixEl ? prefixEl.textContent.trim() : '';

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'section-faq__tab';
      btn.setAttribute('data-faq-tab', handle);
      btn.setAttribute('data-faq-anchor', 'cat-' + anchor);
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-selected', 'false');

      if (prefix.length > 0) {
        var prefixSpan = document.createElement('span');
        prefixSpan.className = 'section-faq__tab-prefix';
        prefixSpan.setAttribute('aria-hidden', 'true');
        prefixSpan.textContent = prefix;
        btn.appendChild(prefixSpan);
      }

      var nameSpan = document.createElement('span');
      nameSpan.className = 'section-faq__tab-name';
      nameSpan.textContent = name;
      btn.appendChild(nameSpan);

      if (showCounts) {
        var countSpan = document.createElement('span');
        countSpan.className = 'section-faq__tab-count';
        countSpan.textContent = String(count);
        btn.appendChild(countSpan);
      }

      container.appendChild(btn);
    }

    if (allCountSpan) {
      allCountSpan.textContent = String(total);
    }
  }

  /* ---------- Mobile tab scroll arrows ---------- */

  function setupTabArrows(section) {
    var wrap = section.querySelector('[data-faq-tabs-wrap]');
    if (!wrap) return;
    var strip = wrap.querySelector('[data-faq-tabs]');
    var prev = wrap.querySelector('[data-faq-tabs-prev]');
    var next = wrap.querySelector('[data-faq-tabs-next]');
    if (!strip || !prev || !next) return;

    function update() {
      var maxScroll = strip.scrollWidth - strip.clientWidth;
      if (maxScroll <= 1) {
        prev.hidden = true;
        next.hidden = true;
        return;
      }
      prev.hidden = strip.scrollLeft <= 1;
      next.hidden = strip.scrollLeft >= maxScroll - 1;
    }

    function scrollByAmount(dir) {
      var amount = Math.max(strip.clientWidth * 0.7, 100);
      strip.scrollBy({ left: dir * amount, behavior: 'smooth' });
    }

    prev.addEventListener('click', function () { scrollByAmount(-1); });
    next.addEventListener('click', function () { scrollByAmount(1); });
    strip.addEventListener('scroll', function () {
      window.requestAnimationFrame(update);
    });
    window.addEventListener('resize', function () {
      window.requestAnimationFrame(update);
    });
    update();
  }

  /* ---------- Search filter ---------- */

  function runSearchFilter(section) {
    var input = section.querySelector('[data-faq-search]');
    var query = input ? input.value.trim().toLowerCase() : '';
    var tabBehavior = section.getAttribute('data-tab-behavior');
    var activeTab = section.getAttribute('data-active-tab') || 'all';
    var items = section.querySelectorAll('[data-acc-item]');
    var visibleCount = 0;

    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var matchesQuery = true;
      if (query.length > 0) {
        var q = item.querySelector('.shared-accordion__question');
        var a = item.querySelector('.shared-accordion__answer');
        var haystack = ((q ? q.textContent : '') + ' ' + (a ? a.textContent : '')).toLowerCase();
        matchesQuery = haystack.indexOf(query) !== -1;
      }

      var matchesTab = true;
      if (tabBehavior === 'filter' && activeTab !== 'all') {
        var cat = item.closest('.faq-category');
        var catName = cat ? cat.getAttribute('data-cat-name') : '';
        matchesTab = catName === activeTab;
      }

      if (matchesQuery && matchesTab) {
        item.classList.remove('is-hidden');
        visibleCount++;
      } else {
        item.classList.add('is-hidden');
      }
    }

    var categories = section.querySelectorAll('.faq-category');
    for (var c = 0; c < categories.length; c++) {
      var category = categories[c];
      var visibleItems = category.querySelectorAll('[data-acc-item]:not(.is-hidden)');
      if (visibleItems.length === 0) {
        category.classList.add('is-hidden');
      } else {
        category.classList.remove('is-hidden');
      }
    }

    var noResults = section.querySelector('[data-faq-no-results]');
    if (noResults) {
      if (visibleCount === 0) {
        noResults.removeAttribute('hidden');
      } else {
        noResults.setAttribute('hidden', '');
      }
    }
  }

  function attachSearch(section) {
    var input = section.querySelector('[data-faq-search]');
    if (!input) return;
    input.addEventListener('input', function () { runSearchFilter(section); });
  }

  /* ---------- Tab interaction ---------- */

  function activateTab(section, tabName) {
    var tabs = section.querySelectorAll('[data-faq-tab]');
    for (var i = 0; i < tabs.length; i++) {
      var tab = tabs[i];
      if (tab.getAttribute('data-faq-tab') === tabName) {
        tab.classList.add('is-active');
        tab.setAttribute('aria-selected', 'true');
      } else {
        tab.classList.remove('is-active');
        tab.setAttribute('aria-selected', 'false');
      }
    }
  }

  function applyFilterToCategories(section, tabName) {
    var categories = section.querySelectorAll('.faq-category');
    for (var i = 0; i < categories.length; i++) {
      if (tabName === 'all' || categories[i].getAttribute('data-cat-name') === tabName) {
        categories[i].classList.add('is-active-cat');
      } else {
        categories[i].classList.remove('is-active-cat');
      }
    }
  }

  function onTabClick(e) {
    var tab = e.target.closest('[data-faq-tab]');
    if (!tab) return;
    var section = tab.closest('[data-section-faq]');
    if (!section) return;

    var tabName = tab.getAttribute('data-faq-tab');
    var tabBehavior = section.getAttribute('data-tab-behavior');

    activateTab(section, tabName);

    if (tabBehavior === 'filter') {
      section.setAttribute('data-active-tab', tabName);
      applyFilterToCategories(section, tabName);
      runSearchFilter(section);
    } else {
      // jumplink mode — scroll to category (or section top for "all")
      if (tabName === 'all') {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        var anchor = tab.getAttribute('data-faq-anchor');
        var target = anchor ? section.querySelector('#' + anchor) : null;
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }
  }

  /* ---------- Init ---------- */

  function initSection(section) {
    if (section.__faqInited) return;
    section.__faqInited = true;
    generateTabs(section);
    setupTabArrows(section);
    attachSearch(section);
  }

  function initAll(scope) {
    var root = scope || document;
    var sections = root.querySelectorAll('[data-section-faq]');
    for (var i = 0; i < sections.length; i++) {
      initSection(sections[i]);
    }
  }

  function init() {
    document.addEventListener('click', onTabClick);
    initAll(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target) initAll(e.target);
  });
})();
